import {
  CurrencyCode,
  enumCurrencyCode,
  enumUnit,
  LoadStatus,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { maybe, calculateNetWeight } from '@rematter/ui-web';
import omit from 'lodash/omit';
import random from 'lodash/random';
import range from 'lodash/range';
import sample from 'lodash/sample';

import { fullLoadFragment } from './fragmentsLoad';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

type MockPhoto = { id: string; url: string; name: string };

type BookingRef =
  | {
      id?: string | null;
      documentNumber?: string | null;
      vesselName?: string | null;
    }
  | null
  | undefined;
type SupplierRef =
  | {
      id?: string | null;
      name?: string | null;
      phone?: string | null;
      email?: string | null;
      addresses?: {
        address?: {
          addressType?: string;
          streetAddress: string;
          city: string;
          state: string;
          postalCode: string;
        };
      }[];
      contacts?:
        | {
            id?: string | null;
            fullName?: string | null;
            email?: string | null;
            phone?: string | null;
          }[]
        | null;
    }
  | null
  | undefined;

type WeightRecord = { value: number; unit: Unit };

type MoneyRecord = { amount: number; currencyCode: CurrencyCode };

export type LoadLineItemRecord = {
  id: string;
  itemId: string | null;
  type: string | null;
  loadId: string | null;
  salesOrderLineId: string | null;
  weights: Record<string, WeightRecord> | null;
  unitPrice: MoneyRecord | null;
  estimatedPrice: { amount: number } | null;

  item:
    | ({ id: string; name: string | null; skuNumber: string | null } & Record<
        string,
        unknown
      >)
    | null;
  recyclerFiles: MockPhoto[];
};

type ReceivedLineItemRecord = {
  id: string;
  loadLineItemId: string;
  itemId: string | null;
  materialType: string | null;
  grossWeight: WeightRecord | null;
  tareWeight: WeightRecord | null;
  netWeight: WeightRecord | null;
  customerGrossWeight: WeightRecord | null;
  customerTareWeight: WeightRecord | null;
  unitPrice: MoneyRecord | null;
  timestamps: { createdAt: string };
};

type DeductionRecord = {
  id: string;
  deductionTypeId: string;
  receivedLineItemId: string | null;
  weightQuantity: number | WeightRecord | null;
  priceQuantity: MoneyRecord | null;
  timestamps: { createdAt: string };
};

export type FullLoadRecord = {
  id: string;
  status: LoadStatus;
  reviewed: boolean;
  loadNumber: string;
  purchaseOrderNumber: string | undefined;
  numberOfMaterials: number;
  netWeight: WeightRecord & { __typename: string };
  totalPrice: MoneyRecord;
  booking: BookingRef;
  supplier: SupplierRef;
  lineItems: LoadLineItemRecord[];
  receivedLineItems?: ReceivedLineItemRecord[];
  deductions?: DeductionRecord[];
  recyclerFiles: MockPhoto[];
  timestamps: { createdAt: string; updatedAt: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & Record<string, any>;

// prettier-ignore
const validPexelIds = ['32098773','31173342','16395151','33032509','13544401','8730438','8266288','31657755','31606428','32728904','32376543','32936971','31657754','20736349','2928544','3945402','28645775','28674821','31657760','31454984','12951466','19923604','30686967','20659445','5531377','7726102','8499859','5263835','19923074','5420831','7725625','4027943','1003993','7726072','14823200','6342279','7725999','8734440','9354912','5264112','7474854','8499885','33146642','31934982','46024','32276112','32658874','31915263'];

const getListOfMockPhotos = (id: string): MockPhoto[] =>
  range(random(2, 6)).map((i) => {
    const pexelId = sample(validPexelIds);
    return {
      id: `${id}-${i}`,
      url: `https://images.pexels.com/photos/${pexelId}/pexels-photo-${pexelId}.jpeg`,
      name: `${id.slice(-5)}-${i + 1}`,
    };
  });

export const loadDetailsQueryAtom = createQueryAtomFactory(
  'customerPortal.loadDetails',
  (params: { id?: string }) => ({
    load: {
      __args: {
        id: params.id ?? '',
      },
      ...fullLoadFragment,
    },
  }),
  {
    select: ({ load }): FullLoadRecord | undefined =>
      maybe(load, (load) => ({
        ...omit(load, ['documentNumber', 'salesOrder', 'reconciliation']),
        booking: load.booking as BookingRef,
        loadNumber: load.documentNumber,
        purchaseOrderNumber: load.salesOrder?.documentNumber,
        numberOfMaterials: load.lineItems?.length ?? 0,
        netWeight: calculateNetWeight(
          load.heavyWeight
            ? { ...load.heavyWeight, __typename: 'Weight' as const }
            : null,
          load.lightWeight
            ? { ...load.lightWeight, __typename: 'Weight' as const }
            : null,
        ) ?? {
          value: 0,
          unit: enumUnit.Pounds,
          __typename: 'Weight' as const,
        },
        supplier: load.salesOrder?.account as SupplierRef,
        totalPrice: {
          amount: load.lineItems.reduce(
            (acc, lineItem) => acc + (lineItem.estimatedPrice?.amount ?? 0),
            0,
          ),
          currencyCode: enumCurrencyCode.USD,
        },
        // TODO: 🟠🟠🟠 get recycler files from api
        recyclerFiles: getListOfMockPhotos(load.id), // load.recyclerFiles,
        lineItems: load.lineItems?.map(({ item, ...lineItem }) => ({
          ...lineItem,
          // TODO: 🟠🟠🟠 get recycler files from api
          recyclerFiles: getListOfMockPhotos(lineItem.id),
          item,
        })),
        receivedLineItems: load.reconciliation?.receivedLineItems,
        deductions: load.reconciliation?.deductions?.map((deduction) => ({
          ...deduction,
          weightQuantity:
            'weightQuantity' in deduction &&
            typeof deduction.weightQuantity === 'object' &&
            deduction.weightQuantity !== null
              ? deduction.weightQuantity.value
              : 'weightQuantity' in deduction
                ? deduction.weightQuantity
                : null,
        })),
      })),
  },
);
